/**
 * usePayPalScript Hook
 *
 * Custom hook for loading the PayPal JavaScript SDK dynamically.
 * Handles script loading, error states, and ensures the script is only loaded once.
 * 
 * The PayPal Client ID is loaded from the server configuration (admin settings).
 *
 * @example
 * ```tsx
 * // Get clientId from store (loaded from server config)
 * const paypalClientId = useStore((state) => state.paypalClientId);
 * const { isLoaded, isLoading, error } = usePayPalScript(paypalClientId);
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage message={error.message} />;
 * if (isLoaded) return <PayPalButtons />;
 * ```
 *
 * @validates Requirements 1.1, 1.2, 1.3
 */

import { useState, useEffect, useRef } from 'react';

/**
 * PayPal SDK configuration constants (currency, intent, locale)
 * Note: clientId is now loaded dynamically from the server config
 */
export const PAYPAL_SDK_CONFIG = {
  currency: 'EUR',
  intent: 'capture',
  locale: 'fr_FR',
} as const;

/**
 * PayPal button style configuration (matching mpsoftsolutions.com)
 */
export const PAYPAL_BUTTON_STYLE = {
  layout: 'vertical' as const,
  color: 'white' as const,
  shape: 'pill' as const,
  label: 'pay' as const,
};

/**
 * PayPal experience context configuration
 */
export const PAYPAL_EXPERIENCE = {
  brand_name: 'MP Soft Solutions',
  user_action: 'PAY_NOW',
  locale: 'fr-FR',
} as const;

/**
 * Return type for usePayPalScript hook
 */
export interface UsePayPalScriptReturn {
  /**
   * Whether the PayPal SDK has finished loading successfully
   */
  isLoaded: boolean;

  /**
   * Whether the PayPal SDK is currently loading
   */
  isLoading: boolean;

  /**
   * Error that occurred during script loading, if any
   */
  error: Error | null;
}

// Script loading states tracked globally to ensure single load
let scriptLoadingPromise: Promise<void> | null = null;
let scriptLoaded = false;
let scriptError: Error | null = null;
let loadedClientId: string | null = null; // Track which clientId was loaded

/**
 * Generates the PayPal SDK URL with the provided client ID and configuration
 * Supports both one-time payments (capture) and subscriptions (vault)
 */
function getPayPalScriptUrl(clientId: string): string {
  const params = new URLSearchParams({
    'client-id': clientId,
    components: 'buttons',
    currency: PAYPAL_SDK_CONFIG.currency,
    'integration-date': '2023-12-01',
    locale: PAYPAL_SDK_CONFIG.locale,
    vault: 'true', // Required for subscriptions, also works with one-time payments
  });

  return `https://www.paypal.com/sdk/js?${params.toString()}`;
}

/**
 * Removes the existing PayPal script and resets state
 * Called when clientId changes to force reload with new clientId
 */
function removePayPalScript(): void {
  const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
  if (existingScript) {
    existingScript.remove();
  }
  // Clear PayPal from window
  if ((window as any).paypal) {
    delete (window as any).paypal;
  }
  // Reset state
  scriptLoadingPromise = null;
  scriptLoaded = false;
  scriptError = null;
  loadedClientId = null;
}

/**
 * Loads the PayPal SDK script dynamically
 * Returns a promise that resolves when the script is loaded
 * If clientId has changed, removes old script and loads new one
 */
function loadPayPalScript(clientId: string): Promise<void> {
  // If clientId changed, remove old script and reload
  if (loadedClientId && loadedClientId !== clientId) {
    console.log(`PayPal clientId changed from ${loadedClientId.substring(0, 10)}... to ${clientId.substring(0, 10)}..., reloading script`);
    removePayPalScript();
  }

  // If already loaded with same clientId, resolve immediately
  if (scriptLoaded && loadedClientId === clientId) {
    return Promise.resolve();
  }

  // If already loading, return the existing promise
  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  // If there was a previous error, reset and try again
  if (scriptError) {
    scriptError = null;
  }

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    // Check if script already exists in DOM with correct clientId
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      const scriptSrc = existingScript.getAttribute('src') || '';
      const scriptClientId = new URLSearchParams(scriptSrc.split('?')[1]).get('client-id');
      
      // If existing script has different clientId, remove it
      if (scriptClientId !== clientId) {
        removePayPalScript();
      } else if ((window as any).paypal) {
        // Script exists with correct clientId and PayPal is ready
        scriptLoaded = true;
        loadedClientId = clientId;
        resolve();
        return;
      } else {
        // Script exists but PayPal not ready, wait for it
        existingScript.addEventListener('load', () => {
          scriptLoaded = true;
          loadedClientId = clientId;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          const error = new Error('Le service de paiement PayPal est temporairement indisponible');
          scriptError = error;
          reject(error);
        });
        return;
      }
    }

    // Create and load the script
    const script = document.createElement('script');
    script.src = getPayPalScriptUrl(clientId);
    script.async = true;
    script.defer = true;
    script.setAttribute('data-sdk-integration-source', 'spellion');

    script.onload = () => {
      scriptLoaded = true;
      loadedClientId = clientId;
      scriptLoadingPromise = null;
      resolve();
    };

    script.onerror = () => {
      const error = new Error('Le service de paiement PayPal est temporairement indisponible');
      scriptError = error;
      scriptLoadingPromise = null;
      reject(error);
    };

    document.body.appendChild(script);
  });

  return scriptLoadingPromise;
}

/**
 * Custom hook for loading the PayPal JavaScript SDK dynamically.
 *
 * Features:
 * - Loads the PayPal SDK script only once (singleton pattern)
 * - Provides loading, loaded, and error states
 * - Configures SDK with EUR currency and fr_FR locale
 * - Handles script loading failures gracefully
 * - Handles missing clientId gracefully (shows configuration error)
 *
 * @param clientId - PayPal Client ID for SDK initialization (from server config)
 * @returns Object containing isLoaded, isLoading, and error states
 *
 * @example
 * ```tsx
 * function PaymentComponent() {
 *   const paypalClientId = useStore((state) => state.paypalClientId);
 *   const { isLoaded, isLoading, error } = usePayPalScript(paypalClientId);
 *
 *   if (isLoading) {
 *     return <div>Chargement du service de paiement...</div>;
 *   }
 *
 *   if (error) {
 *     return <div>Erreur: {error.message}</div>;
 *   }
 *
 *   return isLoaded ? <PayPalButtons /> : null;
 * }
 * ```
 */
export function usePayPalScript(clientId: string): UsePayPalScriptReturn {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef<boolean>(true);
  const lastClientIdRef = useRef<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    // If clientId is empty, don't attempt to load
    if (!clientId || clientId.trim() === '') {
      setIsLoaded(false);
      setIsLoading(false);
      setError(new Error('PayPal Client ID non configuré. Veuillez contacter l\'administrateur.'));
      return;
    }

    // Check if clientId changed - if so, we need to reload
    const clientIdChanged = lastClientIdRef.current !== null && lastClientIdRef.current !== clientId;
    lastClientIdRef.current = clientId;

    // If clientId changed, reset states and reload
    if (clientIdChanged) {
      console.log('PayPal Client ID changed, reloading script...');
      setIsLoaded(false);
      setError(null);
      // Force script reload by resetting global state
      removePayPalScript();
    }

    // If already loaded with same clientId, nothing to do
    if (scriptLoaded && loadedClientId === clientId) {
      setIsLoaded(true);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Start loading the script
    setIsLoading(true);
    setError(null);

    loadPayPalScript(clientId)
      .then(() => {
        if (mountedRef.current) {
          setIsLoaded(true);
          setIsLoading(false);
          setError(null);
        }
      })
      .catch((err: Error) => {
        if (mountedRef.current) {
          setIsLoaded(false);
          setIsLoading(false);
          setError(err);
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, [clientId]);

  return {
    isLoaded,
    isLoading,
    error,
  };
}

/**
 * Reset the PayPal script state (useful for testing)
 */
export function resetPayPalScriptState(): void {
  removePayPalScript();
}
